import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  useGetListsQuery,
  useAddItemMutation,
  useRemoveItemMutation,
} from '../services/bookmarksApi';
import { materialsApi } from '../services/materialsApi';
import styles from '../css/BookmarkButton.module.css';

// variant: 'default' (Watch.js) | 'hero' (HeroCarousel)
// defaultListKey: system_key списка для быстрого добавления при клике (только hero)
const BookmarkButton = ({ materialId, initialBookmark, variant = 'default', defaultListKey = null, onBookmarkChange, onDropdownChange }) => {
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [activeBookmark, setActiveBookmark] = useState(initialBookmark || null);
  const [toast, setToast] = useState(false);
  const wrapperRef = useRef(null);
  const toastTimer = useRef(null);
  const [dropdownAlign, setDropdownAlign] = useState('left');

  const { data: listsData } = useGetListsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [addItem, { isLoading: adding }] = useAddItemMutation();
  const [removeItem, { isLoading: removing }] = useRemoveItemMutation();

  const lists = listsData?.data || [];
  const isHero = variant === 'hero';

  useEffect(() => {
    setActiveBookmark(initialBookmark || null);
  }, [initialBookmark]);

  const changeOpen = useCallback((value) => {
    setOpen(value);
    onDropdownChange?.(value);
  }, [onDropdownChange]);

  // Закрытие по клику вне и по скроллу (для fixed dropdown)
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        changeOpen(false);
      }
    };
    const handleScroll = () => changeOpen(false);
    if (open) {
      document.addEventListener('mousedown', handleClick);
      if (isHero) window.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [open, isHero, changeOpen]);

  useEffect(() => {
    return () => clearTimeout(toastTimer.current);
  }, []);

  const openDropdown = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      setDropdownAlign(spaceRight < 160 ? 'right' : 'left');
    }
    changeOpen(true);
  };

  const showToast = () => {
    setToast(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(false), 3000);
  };

  const setBookmark = (bookmark) => {
    setActiveBookmark(bookmark);
    onBookmarkChange?.(bookmark);
    dispatch(materialsApi.util.invalidateTags(['Materials']));
  };

  const handleListClick = async (list) => {
    const isActive = activeBookmark && activeBookmark.id === list.id;
    try {
      if (isActive) {
        await removeItem({ listId: list.id, materialId }).unwrap();
        setBookmark(null);
      } else {
        await addItem({ listId: list.id, materialId }).unwrap();
        setBookmark({ id: list.id, name: list.name, system_key: list.system_key });
      }
    } catch {
      // ignore
    }
    changeOpen(false);
  };

  const handleRemove = async () => {
    if (!activeBookmark) return;
    try {
      await removeItem({ listId: activeBookmark.id, materialId }).unwrap();
      setBookmark(null);
    } catch {
      // ignore
    }
    changeOpen(false);
  };

  const handleHeroClick = async () => {
    if (!isAuthenticated) {
      showToast();
      return;
    }
    if (activeBookmark) {
      open ? changeOpen(false) : openDropdown();
      return;
    }
    // Не в закладках — сразу добавить в defaultListKey
    if (defaultListKey && lists.length > 0) {
      const defaultList = lists.find((l) => l.system_key === defaultListKey);
      if (defaultList) {
        try {
          await addItem({ listId: defaultList.id, materialId }).unwrap();
          setBookmark({ id: defaultList.id, name: defaultList.name, system_key: defaultList.system_key });
        } catch {
          // ignore
        }
        return;
      }
    }
    openDropdown();
  };

  const handleDefaultClick = () => {
    open ? changeOpen(false) : openDropdown();
  };

  const activeList = activeBookmark ? lists.find((l) => l.id === activeBookmark.id) : null;
  const activeColor = activeList?.color || null;
  const isLoading = adding || removing;

  const buttonLabel = activeBookmark
    ? activeBookmark.name
    : (isHero ? 'Запланировать' : 'В закладки');

  const dropdownContent = (
    <>
      {lists.map((list) => {
        const isActive = activeBookmark && activeBookmark.id === list.id;
        const color = list.color || 'var(--accent-color)';
        return (
          <button
            key={list.id}
            className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
            style={{ '--item-color': color }}
            onClick={() => handleListClick(list)}
          >
            <span className={styles.itemName}>{list.name}</span>
            {isActive && (
              <svg className={styles.check} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </button>
        );
      })}
      {activeBookmark && (
        <>
          <div className={styles.divider} />
          <button className={`${styles.item} ${styles.removeItem}`} onClick={handleRemove}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            <span className={styles.itemName}>Убрать из закладок</span>
          </button>
        </>
      )}
    </>
  );

  return (
    <div className={`${styles.wrapper} ${isHero ? styles.wrapperHero : ''}`} ref={wrapperRef}>
      <button
        className={`${styles.button} ${isHero ? styles.buttonHero : ''} ${activeBookmark ? styles.active : ''}`}
        onClick={isHero ? handleHeroClick : handleDefaultClick}
        disabled={isLoading}
        style={activeColor ? { '--active-color': activeColor } : undefined}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={activeBookmark ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
        <span className={styles.buttonLabel}>{buttonLabel}</span>
        <span className={styles.chevronDivider} />
        <svg className={styles.chevron} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Тост — только hero, не-авторизован */}
      {isHero && toast && (
        <div className={styles.toast}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <span>Необходимо авторизоваться, чтобы добавлять в закладки</span>
        </div>
      )}

      {/* authHint dropdown — только default, не-авторизован */}
      {!isHero && open && !isAuthenticated && (
        <div className={styles.authHint}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <span>Необходимо авторизоваться, чтобы добавлять в закладки</span>
        </div>
      )}

      {/* Dropdown — авторизован */}
      {open && isAuthenticated && (
        <div
          className={styles.dropdown}
          style={dropdownAlign === 'right' ? { left: 'auto', right: 0 } : { left: 0, right: 'auto' }}
        >
          {dropdownContent}
        </div>
      )}
    </div>
  );
};

export default BookmarkButton;
